/// <amd-module name="TFS/DRITools/Components/Callgraph/WaterfallTable" />
import d3 = require("d3");
import * as React from "react";

// Office Fabric
import { DetailsListLayoutMode, DetailsRow, IColumn, IDetailsRowProps, IGroupDividerProps, SelectionMode } from "OfficeFabric/DetailsList";
import { Label } from "OfficeFabric/Label";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { Tooltip } from "VSSUI/TooltipEx";
import { Panel } from "VSSUI/Panel";

// VSS
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

// VSSUI
import { PivotBar, PivotBarItem } from "VSSUI/PivotBar";
import { VssDetailsList, VssDetailsListGroupHeader, VssDetailsListRowStyle, VssDetailsListTitleCell } from "VSSUI/VssDetailsList";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";

// DRI Tools
import * as Resources from "TFS/DRITools/Resources";
import { parseDate } from "TFS/DRITools/util/Date";
import { Obj, RequestNode } from "TFS/DRITools/util/RequestNode";

export interface IWaterfallTableProps extends IVssComponentProperties {
    data: RequestNode[];
    startTime: number;
    barHeight: number;
    depth: number;
    selectedColumns: string[];
    parseTrace: boolean;
    waterfallLength: number;
}

export interface IWaterfallTableState extends IVssComponentProperties {
    isPanelVisible: boolean;
    clickedData: RequestNode | undefined;
    selectedPivot: string;
    selectedTabId: string;
    loadedList: boolean;
}

interface DetailsListItem {
    key: string;
    name: string;
    value: string | JSX.Element;
    tracePoint: string;
}

class WaterfallTable extends VssComponent<IWaterfallTableProps, IWaterfallTableState> {
    private _callout: HTMLElement | null;
    private _columns: IColumn[] = [];
    // Color scale for gradient
    private _colorScale = d3
        .scaleLinear<String>()
        .domain([0, 5000, 10000, Number.MAX_SAFE_INTEGER])
        .range([Resources.Callgraph_Accent2, Resources.Callgraph_Accent3, Resources.Callgraph_Accent1, Resources.Callgraph_Accent1]);

    private _detailsColumns = {
        activityLog: [
            {
                key: "activityLog-Key",
                fieldName: "activityLog-key",
                name: "",
                onRender: (item: string) => {
                    return <VssDetailsListTitleCell primaryText={item} />;
                },
                minWidth: 100,
                maxWidth: 150,
                isResizable: true
            },
            {
                key: "activityLog-value",
                fieldName: "activityLog-value",
                name: "",
                onRender: (item: string) => {
                    const clickedData: RequestNode | undefined = this.state.clickedData;
                    if (clickedData) {
                        const value = clickedData.ActivityLog[item];
                        if (value instanceof Date) {
                            return <VssDetailsListTitleCell primaryText={parseDate(value)} />;
                        }
                        return <VssDetailsListTitleCell primaryText={value.toString()} />;
                    }
                },
                minWidth: 700,
                isMultiline: true,
                isResizable: true
            }
        ],
        iisLog: [
            {
                key: "iisLog-key",
                fieldName: "iisLog-key",
                name: "",
                onRender: (item: string) => {
                    return <span>{item}</span>;
                },
                minWidth: 100,
                maxWidth: 150,
                isResizable: true
            },
            {
                key: "iisLog-value",
                fieldName: "iisLog-value",
                name: "",
                onRender: (item: string) => {
                    const clickedData: RequestNode | undefined = this.state.clickedData;
                    if (clickedData) {
                        const value = clickedData.IisLog[item];
                        if (value instanceof Date) {
                            return <VssDetailsListTitleCell primaryText={parseDate(value)} />;
                        }
                        return <VssDetailsListTitleCell primaryText={value.toString()} />;
                    }
                },
                minWidth: 700,
                isMultiline: true,
                isResizable: true
            }
        ],
        productTrace: [
            {
                key: "productTrace-key",
                fieldName: "productTrace-key",
                name: "",
                onRender: (item: DetailsListItem) => {
                    return <VssDetailsListTitleCell primaryText={item.name} />;
                },
                minWidth: 100,
                maxWidth: 150
            },
            {
                key: "productTrace-value",
                fieldName: "productTrace-value",
                name: "",
                onRender: (item: DetailsListItem) => {
                    return <VssDetailsListTitleCell primaryText={item.value.toString()} />;
                },
                minWidth: 700,
                isMultiline: true,
                isResizable: true
            }
        ],
        slowTrace: [
            {
                key: "slowTrace",
                fieldName: "slowTrace",
                name: "",
                onRender: (item: DetailsListItem) => {
                    return item;
                },
                minWidth: 700
            }
        ]
    };

    constructor(props: IWaterfallTableProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isPanelVisible: false,
            clickedData: undefined,
            selectedPivot: Resources.Callgraph_ActivityLog,
            selectedTabId: Resources.Callgraph_ActivityLog,
            loadedList: false
        };

        this._populateTableColumns();
    }

    public _drawTree(data: RequestNode) {
        const padding = {
            paddingLeft: (data.depth - 1) * 16 + "px"
        } as React.CSSProperties;

        return (
            <div style={padding} className="childTree">
                <span>{data.ActivityLog.Service ? data.ActivityLog.Service : data.IisLog ? data.IisLog.Service : data.ProductTrace[0].Service}</span>
            </div>
        );
    }

    // Draw waterfall bars
    public _drawWaterfall(data: RequestNode, width: number) {
        const that = this;
        const scaleFactor: number = width / that.props.waterfallLength;
        const gradient = {
            fill: this._colorScale(data.ActivityLog.ExecutionTime / 1000)
        } as React.CSSProperties;

        return (
            <div className="waterfall-rect">
                <svg width="100%" height={that.props.barHeight}>
                    <g
                        transform={
                            "translate(" +
                            (Date.parse(data.ActivityLog.StartTime) + data.ActivityLog.StartTime.getMilliseconds() - that.props.startTime) *
                                scaleFactor +
                            ",0)"
                        }
                    >
                        <rect width={(data.ActivityLog.ExecutionTime / 1000) * scaleFactor} height="100%" style={gradient} />
                        <text x="10" y={that.props.barHeight / 2}>
                            {Math.floor(data.ActivityLog.ExecutionTime / 1000) + " ms"}
                        </text>
                    </g>
                </svg>
            </div>
        );
    }

    public render(): JSX.Element {
        const { isPanelVisible, clickedData, selectedPivot } = this.state;

        return (
            <div className="flex flex-grow waterfall-list">
                <VssDetailsList
                    items={this.props.data}
                    selectionPreservedOnEmptyClick={false}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    columns={this._columns}
                    onRenderRow={(props: IDetailsRowProps) => {
                        return (
                            <div onClick={() => this._onShowMenuClicked(props.item)}>
                                <DetailsRow {...props} />
                            </div>
                        );
                    }}
                />
                <div ref={div => (this._callout = div)} />
                {isPanelVisible && (
                    <Panel size={ContentSize.Large} onDismiss={() => this._setPanelState(false)}>
                        <PivotBar
                            selectedPivot={selectedPivot}
                            onPivotClicked={this._onPivotChanged}
                            isCommandBarSticky={true}
                            isPivotBarContentScrollable={false}
                        >
                            <PivotBarItem itemKey={Resources.Callgraph_ActivityLog} name={Resources.Callgraph_ActivityLog} className="pivot-height">
                                {clickedData && !clickedData.ActivityLog.ActivityId ? (
                                    <div className="no-list-item">{Resources.Callgraph_NoData}</div>
                                ) : (
                                    <VssDetailsList
                                        items={clickedData && clickedData.ActivityLog.ActivityId ? Object.keys(clickedData.ActivityLog) : []}
                                        selectionPreservedOnEmptyClick={true}
                                        isHeaderVisible={false}
                                        selectionMode={SelectionMode.none}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        columns={this._detailsColumns.activityLog}
                                    />
                                )}
                            </PivotBarItem>
                            <PivotBarItem itemKey={Resources.Callgraph_IisLog} name={Resources.Callgraph_IisLog} className="pivot-height">
                                {clickedData && !clickedData.IisLog.ActivityId ? (
                                    <div className="no-list-item">{Resources.Callgraph_NoData}</div>
                                ) : (
                                    <VssDetailsList
                                        items={clickedData && clickedData.IisLog.ActivityId ? Object.keys(clickedData.IisLog) : []}
                                        selectionPreservedOnEmptyClick={true}
                                        isHeaderVisible={false}
                                        selectionMode={SelectionMode.none}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        columns={this._detailsColumns.iisLog}
                                    />
                                )}
                            </PivotBarItem>
                            <PivotBarItem itemKey={Resources.Callgraph_ProductTrace} name={Resources.Callgraph_ProductTrace} className="pivot-height">
                                {clickedData && clickedData.ProductTrace.length == 0 ? (
                                    <div className="no-list-item">{Resources.Callgraph_NoData}</div>
                                ) : (
                                    <VssDetailsList
                                        items={clickedData && clickedData.ProductTrace.length > 0 ? clickedData.ProductTrace : []}
                                        groups={clickedData && clickedData.productTraceGroups ? clickedData.productTraceGroups : []}
                                        isHeaderVisible={false}
                                        selectionMode={SelectionMode.none}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        columns={this._detailsColumns.productTrace}
                                        rowStyle={VssDetailsListRowStyle.oneLine}
                                        groupProps={{
                                            onRenderHeader: this._onRenderHeader
                                        }}
                                    />
                                )}
                            </PivotBarItem>
                            <PivotBarItem
                                itemKey={Resources.Callgraph_SlowTrace}
                                name={Resources.Callgraph_SlowTrace}
                                hidden={!clickedData || !clickedData.hasSlowCommandTrace}
                                className="pivot-height"
                            >
                                <VssDetailsList
                                    items={clickedData && clickedData.hasSlowCommandTrace && clickedData.trace ? clickedData.trace : []}
                                    isHeaderVisible={false}
                                    columns={this._detailsColumns.slowTrace}
                                    selectionMode={SelectionMode.none}
                                />
                            </PivotBarItem>
                        </PivotBar>
                    </Panel>
                )}
                
            </div>
        );
    }

    public componentDidUpdate(): void {
        this._populateTableColumns();
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._populateTableColumns();
    }

    private _setPanelState = (isVisible: boolean): void => {
        if (isVisible) {
            this.setState({
                isPanelVisible: isVisible,
                clickedData: undefined
            });
        } else {
            this.setState({
                isPanelVisible: isVisible
            });
        }
    };

    private onSelectedTabChanged = (newTabId: string) => {
        this.setState({
            selectedtabId: newTabId
        });
    };

    private _onShowMenuClicked = (item: RequestNode) => {
        if (item == this.state.clickedData) {
            this._setPanelState(!this.state.isPanelVisible);
        } else {
            if (item.ProductTrace.length > 0 && !("tracePoint" in item.ProductTrace[0])) {
                item.ProductTrace = this._populateProductTrace(item);
            }
            this.setState({
                isPanelVisible: true,
                clickedData: item
            });
        }
    };

    private _onPivotChanged = (ev: React.MouseEvent<HTMLElement>, pivotKey: string) => {
        this.setState({
            selectedPivot: pivotKey
        });
    };

    private _populateTableColumns(): void {
        this._columns = [];
        let usedWidth: number = this.props.depth < 2 ? 40 : this.props.depth * 16 + 16;
        this._columns.push({
            key: "Service",
            fieldName: "Service",
            name: "Service",
            onRender: (item: RequestNode) => {
                return this._drawTree(item);
            },
            minWidth: usedWidth,
            isResizable: true
        });
        usedWidth += 16;
        for (const column of this.props.selectedColumns) {
            const width: number =
                column === Resources.Callgraph_Command
                    ? 317
                    : column === Resources.UsageInsights_StartTime
                    ? 160
                    : column === Resources.Callgraph_ActivityId
                    ? 250
                    : column === Resources.Callgraph_ScaleUnit
                    ? 100
                    : 75;
            this._columns.push({
                key: column,
                fieldName: column,
                name: column,
                isSortedDescending: column === Resources.UsageInsights_StartTime,
                onRender: (item: RequestNode) => {
                    let text = "";
                    if (item.ActivityLog.ActivityId) {
                        text = item.ActivityLog[column] instanceof Date ? parseDate(item.ActivityLog[column]) : item.ActivityLog[column].toString();
                    } else if (item.IisLog.ActivityId) {
                        text =
                            column === Resources.Callgraph_Command
                                ? item.IisLog.cs_uri_stem
                                : column === Resources.UsageInsights_StartTime
                                ? parseDate(item.IisLog.PreciseTimeStamp)
                                : item.IisLog[column];
                    } else {
                        text =
                            column === Resources.Callgraph_Command
                                ? item.ProductTrace[0].EventMessage
                                : column === Resources.UsageInsights_StartTime
                                ? parseDate(item.ProductTrace[0].PreciseTimeStamp)
                                : item.ProductTrace[0][column];
                    }
                    return (
                        <Tooltip text={text} id="extendedText">
                            <div
                                className={
                                    column == Resources.Callgraph_ActivityStatus
                                        ? text === "1"
                                            ? "anchor anchor-red"
                                            : text == "2"
                                            ? "anchor anchor-yellow"
                                            : "anchor-green"
                                        : ""
                                }
                            >
                                <VssDetailsListTitleCell aria-describedby="extendedText" primaryText={text} />
                            </div>
                        </Tooltip>
                    );
                },
                minWidth: width,
                maxWidth: 500,
                isResizable: true
            });
            usedWidth += width + 16;
        }

        usedWidth += 35 + 16;

        const waterfallWidth: number = (document.getElementsByClassName("callgraph-hub")[0] as HTMLElement).offsetWidth - usedWidth - 20;

        this._columns.push(
            {
                key: "SlowCommand",
                fieldName: "SlowCommand",
                name: Resources.Callgraph_SlowTrace,
                onRender: (item: RequestNode) => {
                    if (item.hasSlowCommandTrace) {
                        return (
                            <VssIcon
                                iconType={VssIconType.fabric}
                                iconName={"Clock"}
                                onClick={() => {
                                    this.setState({ selectedPivot: Resources.Callgraph_SlowTrace });
                                }}
                            />
                        );
                    }
                    return <div />;
                },
                minWidth: 35,
                isResizable: true
            },
            {
                key: "Waterfall",
                fieldName: "Waterfall",
                name: "Waterfall",
                onRender: (item: RequestNode) => {
                    if (item.ActivityLog.ActivityId) {
                        return this._drawWaterfall(item, waterfallWidth - 80);
                    }
                    return null;
                },
                minWidth: waterfallWidth,
                isResizable: true
            }
        );
    }

    private _populateProductTrace(item: RequestNode): DetailsListItem[] {
        let prevIndex = 0;
        const productTrace: DetailsListItem[] = [];
        for (let i = 0; i < item.ProductTrace.length; i++) {
            const pt: Obj = item.ProductTrace[i];
            const keys: string[] = Object.keys(pt);
            for (let j = 0; j < keys.length; j++) {
                const key = keys[j];
                let value: string | JSX.Element;
                value = pt[key] instanceof Date ? parseDate(pt[key]) : pt[key].toString();
                productTrace.push({
                    key: i + "_key" + key.toString(),
                    name: key.toString(),
                    value: value,
                    tracePoint: pt.Tracepoint
                });
            }
            if (!item.productTraceGroups) {
                item.productTraceGroups = [];
            }
            item.productTraceGroups.push({
                key: pt.Tracepoint + "_" + i,
                name: pt.Tracepoint + "_" + i,
                data: pt,
                isCollapsed: true,
                startIndex: prevIndex,
                count: keys.length
            });
            prevIndex = prevIndex + keys.length;
        }
        return productTrace;
    }

    /**
     * Custom render method for group headers.
     */
    private _onRenderHeader = (props: IGroupDividerProps): JSX.Element => {
        if (!props || !props.group) {
            return <div />;
        }

        const { data } = props.group;
        const message = data.Message.length < 200 ? data.Message : data.Message.substring(0, 200);
        return (
            <div
                className="vss-DetailsList--groupHeaderText"
                onClick={() => {
                    if (!props || !props.group || !props.onToggleCollapse) {
                        return;
                    }
                    props.onToggleCollapse(props.group);
                }}
            >
                <div className="flex flex-grow">
                    <div className="flex-column">
                        <Button subtle={true} iconProps={{ iconName: props.group.isCollapsed ? "ChevronUp" : "ChevronDown" }} />
                    </div>
                    <div className="flex-column">
                        <Label>{data.Tracepoint}</Label>
                    </div>
                </div>
                <VssDetailsListGroupHeader text={message} />
            </div>
        );
    };
}

export default WaterfallTable;
